import React, { useEffect, useState, useRef, createRef } from "react";
import { isMaterialOrGD } from "../../../materialCommitment/helpers/Categories";
import {
  fillAllFieldsNotification,
  reportDraftSavedSuccessNotification,
  showErrorTextNotification,
  startedSavingReportNotification,
  reportSavedSuccessNotification
} from "../../../materialCommitment/helpers/Notifications";
import OperationBody from "./OperationBody";
import OperationHeader from "./OperationHeader";
import moment from "moment";
import Modal, { ModalHeader } from "../../../reusable/Modal";
import Button, { SecondaryButton } from "../../../reusable/Button";

const OperationReport = ({
  operation,
  startingDate,
  endingDate,
  commitment,
  sendReport,
  reportsReady,
  setReportsReady,
  editing,
  reportId,
  operationRef,
  unfinishedReports,
  useDraft,
  setUseDraft
}) => {
  const [collapsed, setCollapsed] = useState(
    !!editing || !isMaterialOrGD(commitment.commitmentType)
  );
  const [meters, setMeters] = useState(
    operation.meters || operation.reportMeters
  );
  const [reportText, setReportText] = useState(
    operation.reportText_fi_FI || ""
  );
  const [progress, setProgress] = useState(operation.progress || "");

  const meterRefs = useRef([]);
  const progressContainerRef = useRef("");
  const textContainerRef = useRef("");
  const [operationDraft, setOperationDraft] = useState(null);
  // Initialize meter references
  if (meterRefs.current.length !== meters.length) {
    meterRefs.current = Array(meters.length)
      .fill()
      .map((_, i) => meterRefs.current[i] || createRef());
  }
  //Check if there are any operation reports with "draft" statuses, if found suggest the user to continue filling the newest draft
  useEffect(() => {
    if (operation.reports && operation.reports.length) {
      const draft = operation.reports.find(r => r.status === "draft");
      setOperationDraft(draft);
    }
  }, [operation]);

  useEffect(() => {
    //Report draft exists, user chose to continue filling that
    if (
      useDraft &&
      useDraft.length &&
      operationDraft &&
      [operationDraft.id, "all"].some(id => useDraft.includes(id))
    ) {
      setProgress(operationDraft.progress);
      setMeters(operationDraft.reportMeters);
      setReportText(operationDraft.reportText_fi_FI);
    }
  }, [useDraft, operationDraft]);

  // A helper function that negates the first render in useEffect when the page is loaded.
  // Used so that the missing input errors are not shown unecessarily when the page is loaded initially
  const useFirstRender = () => {
    const firstRender = useRef(true);
    useEffect(() => {
      firstRender.current = false;
    }, []);
    return firstRender.current;
  };

  const firstRender = useFirstRender();

  useEffect(() => {
    if (!firstRender) {
      if (sendReport !== "") {
        saveReport(sendReport);
      } else {
        validateReport();
      }
    }
  }, [unfinishedReports, sendReport]);

  // Helper function to check if both meters in a pair are empty
  const hasFilledPair = pairId => {
    let hasFilledMeter = false;
    let hasEmptyMeter = false;

    // Iterate over meters to check their filled status in the pair
    meters.forEach(meter => {
      if (meter.meterPairId === pairId) {
        if (meter.currentLevel) {
          hasFilledMeter = true;
        } else {
          hasEmptyMeter = true;
        }
      }
    });
    return hasFilledMeter && hasEmptyMeter;
  };

  const validateReport = () => {
    const unvalidatedMeters = [];

    // Iterate over meters and apply red borders to meters that are individual and not filled or in pairs where both meters are empty
    meters.forEach((meter, index) => {
      if (
        !meter.currentLevel &&
        (!meter.meterPairId || !hasFilledPair(meter.meterPairId))
      ) {
        meterRefs.current[index].current.style.border = "1px solid red";
        unvalidatedMeters.push(meter);
      } else {
        meterRefs.current[index].current.style.border = "none";
      }
    });

    // Handle progress error indicator
    progress && progress !== "none"
      ? (progressContainerRef.current.style.border = "none")
      : (progressContainerRef.current.style.border = "1px solid red");

    // Handle Text input error indicator
    reportText
      ? (textContainerRef.current.style.border = "none")
      : (textContainerRef.current.style.border = "1px solid red");

    if (!reportText || !progress || progress === "none" || unvalidatedMeters.length > 0) {
      return false;
    }

    return true;
  };

  useEffect(() => {
    let ready = true;
    meters.map(meter => {
      if (!meter.currentLevel) {
        ready = false;
      }
    });
    if (!reportText || !progress || progress === "none") {
      ready = false;
    }

    setReportsReady(
      {
        id: operation.operationId,
        name: operation.operationTitle_fi_FI,
        meters: meters,
        reportText: reportText,
        progress: progress
      },
      ready
    );
  }, [meters, reportText, progress]);

  const saveReport = async status => {
    if (status !== "draft" && !validateReport()) {
      fillAllFieldsNotification();
      return;
    }

    startedSavingReportNotification();

    let id = reportId || "";
    let version = operation.version || undefined;
    if (useDraft && useDraft.length && !useDraft.includes("none")) {
      id = Number(operationDraft.id);
      version = operationDraft.version;
    }

    //If saving as draft and commitmentOperationMeterRefId is missing -> must be added in order to edit the draft meters later
    let reportMeters = meters;
    if (status === "draft") {
      reportMeters = meters.map(m => {
        if (!m.commitmentOperationMeterRefId && m.meterId) {
          return { ...m, commitmentOperationMeterRefId: m.meterId };
        }
        return m;
      });
    }

    const response = await fetch(
      "/o/commitment2050-service/commitmentapi/reports",
      {
        method: "POST",
        body: JSON.stringify({
          reportTitle_fi_FI: `${moment(startingDate).format(
            "DD.MM.YYYY"
          )} - ${moment(endingDate).format("DD.MM.YYYY")} ${
            operation.operationTitle_fi_FI
          }`,
          commitmentArticleId: commitment.id,
          commitmentOperationRefId:
            operation.operationId || operation.commitmentOperationRefId,
          createdByUserId: commitment.createdByUserId,
          operationTitle_en_US: operation.operationTitle_en_US,
          operationTitle_fi_FI: operation.operationTitle_fi_FI,
          operationTitle_sv_SE: operation.operationTitle_sv_SE,
          organizationId: commitment.organizationId,
          organizationName: commitment.organizationName,
          progress: progress,
          /**
           * Default report dates to midday to prevent date changing to prev/next day when parsing the date to different timezone.
           */
          reportStartDate: moment(startingDate)
            .startOf("day")
            .hour(12)
            .toISOString(),
          reportEndDate: moment(endingDate)
            .startOf("day")
            .hour(12)
            .toISOString(),
          reportMeters: reportMeters,
          reportText_fi_FI: reportText,
          id: id,
          version: version,
          status: status
        }),
        headers: {
          Accept: "application/json, text/plain, */*",
          "Content-Type": "application/json",
          "Cache-Control": "no-cache"
        },
        credentials: "include"
      }
    );

    //response.ok includes statuscodes from 200 to 299.
    if (!response.ok) {
      showErrorTextNotification("Raportin tallennuksessa tapahtui virhe");
      return;
    }

    if (status === "draft") {
      reportDraftSavedSuccessNotification();
    } else {
      reportSavedSuccessNotification();
    }
  };
  return (
    <div>
      {operationDraft &&
        useDraft &&
        !collapsed &&
        ![operationDraft.id, "all", "none"].some(id =>
          useDraft.includes(id)
        ) && (
          setUseDraft(isMaterialOrGD ? "all" : ids => [...ids, operationDraft.id])
        )}
      <OperationHeader
        operation={operation}
        startingDate={startingDate}
        endingDate={endingDate}
        setCollapsed={setCollapsed}
        editing={editing}
      />
      {!collapsed && (
        <OperationBody
          operationRef={operationRef}
          key={operation.operationId}
          commitment={commitment}
          operation={operation}
          progress={progress}
          startingDate={startingDate}
          endingDate={endingDate}
          setProgress={setProgress}
          setCollapsed={setCollapsed}
          saveReport={saveReport}
          validateReport={validateReport}
          meters={meters}
          reportText={reportText}
          setMeters={setMeters}
          setReportText={setReportText}
          reportId={operation.id}
          editing={editing}
          progressContainerRef={progressContainerRef}
          textContainerRef={textContainerRef}
          meterRefs={meterRefs}
        />
      )}
    </div>
  );
};
export default OperationReport;

const UseDraftConfirmationModal = ({
  setUseDraft,
  draftId,
  isMaterialOrGD
}) => {
  const [show, setShow] = useState(true);
  return (
    <Modal
      show={show}
      background="var(--light-grey"
      dialogClassName="react-bs-modal-root"
    >
      <ModalHeader>
        <div>
          <h2 style={{ margin: 0 }}>Luonnos löydetty</h2>
        </div>
      </ModalHeader>
      <div>
        Kyseisestä raportista on olemassa luonnos.<br></br>
        Haluatko jatkaa luonnoksen täyttämistä?
      </div>
      <Button
        /**
         * material or GD's display all reports same time, so insert  "all" to apply all drafts without prompting the usage of individual drafts.
         * In other forms, such as personal reports insert id's of found drafts to be used
         */
        onClick={() => {
          setUseDraft(isMaterialOrGD ? "all" : ids => [...ids, draftId]);
          setShow(false);
        }}
      >
        Jatka luonnoksen täyttämistä
      </Button>

      <SecondaryButton
        onClick={() => {
          if (isMaterialOrGD) {
            setUseDraft("none");
          }
          setShow(false);
        }}
      >
        Tee uusi raportti
      </SecondaryButton>
    </Modal>
  );
};



// WEBPACK FOOTER //
// ./src/components/profile/reports/components/OperationReport.jsx